import React, { ChangeEvent, useCallback, useEffect } from "react";
import { ContentWrapper, ContinueButton, Form, InputBox, ItemWrapper } from "../styles";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import CustomSelect from "../../../../components/common/CustomSelect";
import { Input } from "../../../../components/common/Input";
import { Button } from "../../../../components/common/Button";
import { savedPayoutV2Data } from "../../redux/formDataSlice";
import { countryPhoneCodes, TZSPayouts } from "../../const";

type SelectedBankObject = {
    label: string;
    value: string;
    originalCode?: string;
};

type TZSBeneficiaryTypes = {
    handleNext?: () => void;
    handlePrevious?: () => void;
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    bankCode?: string;
    selectedBank?: SelectedBankObject;
    description?: string;
    bic?: string;
};

const Beneficiary = ({ handleNext, handlePrevious }: TZSBeneficiaryTypes) => {
    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        watch
    } = useForm<TZSBeneficiaryTypes>();

    const dispatch = useAppDispatch();

    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const bankTypeWatch = watch("bankCode")

    useEffect(() => {
        if (getSavedData) {
            Object.keys(getSavedData).forEach((key) => {
                setValue(
                    key as keyof TZSBeneficiaryTypes,
                    getSavedData[key as keyof TZSBeneficiaryTypes],
                );
                setValue('bankName', getSavedData.bankName)
            });
        }
    }, [getSavedData, setValue]);

    const onSubmit: SubmitHandler<TZSBeneficiaryTypes> = useCallback(
        (data) => {

            const combinedData = {
                ...data,
                bankName: data.bankName,
                bankCode: data.bankCode
            };

            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch],
    );

    const isTzsWithMomo = bankTypeWatch !== "TZ-BANK-B2C" &&
        TZSPayouts.some(item => item.value === getSavedData.bankCode);

    return (
        <ContentWrapper>
            <Form>
                <InputBox>
                    <Controller
                        name="bankCode"
                        control={control}
                        rules={{
                            required: "Please input the beneficiary's Bank name",
                        }}
                        render={({ field }) => (
                            <CustomSelect
                                placeholder={"Select bank"}
                                $width="100%"
                                field={field}
                                disableLabel
                                value={getSavedData?.bankCode}
                                showSearch
                                lightBorder={true}
                                disabled
                                options={TZSPayouts}
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.bankName?.message}
                    </span>
                </InputBox>

                {bankTypeWatch === "TZ-BANK-B2C" && <InputBox>
                    <Controller
                        name="bic"
                        control={control}
                        rules={{
                            required:
                                "Please enter the beneficiary's bank Business Identifier Code",
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="BIC"
                                field={field}
                                labelColor="#FFFFFF"
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.bic?.message}

                    </span>

                </InputBox>}

                <InputBox>
                    <Controller
                        name="accountNumber"
                        control={control}
                        rules={{
                            required: "Please enter the beneficiary's account number"
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                            <Input
                                type="number"
                                label={"Account number"}
                                labelColor="#FFFFFF"
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    let newValue = e.target.value;
                                    if (isTzsWithMomo && newValue.startsWith('0')) {
                                        newValue = newValue.substring(1);
                                    }
                                    onChange(e.target.value);
                                }}
                                value={value}
                                {...field}
                                disableLabel
                                showCountryPhoneCode={isTzsWithMomo}
                                paddingForPrefix={isTzsWithMomo && "3.5rem"}
                                countryPhoneCode={isTzsWithMomo && countryPhoneCodes["TZS"]}
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountNumber?.message}
                    </span>

                </InputBox>

                <InputBox>
                    <Controller
                        name="accountName"
                        control={control}
                        rules={{
                            required: "Please enter the beneficiary's account name",
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Account name"
                                labelColor="#FFFFFF"
                                field={field}
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountName?.message}
                    </span>

                </InputBox>

                <InputBox>
                    <Controller
                        name="description"
                        control={control}
                        rules={{
                            required: false,
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Payment description (optional)"
                                field={field}
                                labelColor="#FFFFFF"
                                disableLabel
                            />
                        )}
                    />
                </InputBox>

                <ContinueButton>
                    <span className="form-label">
                        Funds should arrive within 1-3 working days.
                    </span>

                    <ItemWrapper>
                        <Button
                            onClick={handlePrevious}
                            label="Prev"
                            height="48px"
                            theme="alternate"
                            width="203px"
                        />

                        <Button
                            onClick={handleSubmit(onSubmit)}
                            label="Continue"
                            height="48px"
                        />
                    </ItemWrapper>
                </ContinueButton>
            </Form>
        </ContentWrapper>
    )
}

export default Beneficiary